import { Vulnerability, PaginationInfo, VulnerabilityBannerResponse, RemediationSummary, RemediationItemProgress } from '../Utils/types';

const getVulnerabilityRowKey = (
  item: {
    softwareAssetInventoryId?: string;
    cveId?: string;
    packageIdentifier?: string;
    version?: string;
    title?: string;
    cveUrl?: string;
  },
  source: string,
  page: number,
  index: number,
) => {
  const compositeKey = [source, item.softwareAssetInventoryId, item.cveId, item.packageIdentifier, item.version, item.title, item.cveUrl]
    .filter(Boolean)
    .join('::');

  return compositeKey || `${source}::page-${page}::row-${index}`;
};

const getAgeInDays = (publishedDateValue?: string) => {
  if (!publishedDateValue) return 0;
  const publishedDate = new Date(publishedDateValue);
  if (Number.isNaN(publishedDate.getTime())) return 0;
  const today = new Date();
  return Math.floor((today.getTime() - publishedDate.getTime()) / (1000 * 60 * 60 * 24));
};

export async function getPatchifiBanner(
  selectedTenantId: string,
  getVulnerabilityBanner: (tenantId: string) => Promise<VulnerabilityBannerResponse>,
) {
  const response = await getVulnerabilityBanner(selectedTenantId);
  return response;
}

export async function getPatchifiVulnerabilities(params: {
  selectedTenantId: string;
  page: number;
  itemsPerPage: number;
  selectedSeverity: string;
  selectedKev: string;
  selectedEpss: string;
  searchTerm: string;
  getSoftwareAssets: (params: any) => Promise<any>;
}) {
  let kevExploited: boolean | undefined = undefined;
  if (params.selectedKev === 'Yes') kevExploited = true;
  if (params.selectedKev === 'No') kevExploited = false;

  let epssThreshold: number | undefined = undefined;
  if (params.selectedEpss === '>0.5') epssThreshold = 0.5;
  if (params.selectedEpss === '>0.7') epssThreshold = 0.7;

  const response = await params.getSoftwareAssets({
    tenantId: params.selectedTenantId,
    isSupported: true,
    pageNumber: params.page,
    pageSize: params.itemsPerPage,
    severity: params.selectedSeverity || undefined,
    kevExploited,
    epssThreshold,
    search: params.searchTerm || undefined,
  });

  if (response?.isSuccess && response?.data) {
    const mappedData: Vulnerability[] = response.data.map((item: any, index: number) => {
      return {
        rowKey: getVulnerabilityRowKey(
          {
            softwareAssetInventoryId: item.softwareAssetInventoryId,
            cveId: item.cveId,
            packageIdentifier: item.packageIdentifier,
            version: item.version,
            title: item.name,
            cveUrl: item.cveUrl,
          },
          'Patchifi',
          params.page,
          index,
        ),
        softwareAssetInventoryId: item.softwareAssetInventoryId,
        cveId: item.cveId || 'N/A',
        cveUrl: item.cveUrl,
        title: item.name,
        severity: item.baseSeverity || 'Unknown',
        cvssScore: item.cvss ? parseFloat(item.cvss) : 0,
        epssScore: item.epssScore ?? 0,
        kev: item.isKEV,
        affectedDevicesCount: item.installedEndpoints + item.installedServers,
        ageInDays: getAgeInDays(item.cveDatePublished),
        description: item.descriptions,
        version: item.version,
        packageIdentifier: item.packageIdentifier,
      };
    });

    return {
      vulnerabilities: mappedData,
      paginationInfo: response.pagination || {
        pageNumber: params.page,
        pageSize: params.itemsPerPage,
        totalRecords: mappedData.length,
        totalPages: 1,
        hasPreviousPage: false,
        hasNextPage: false,
      },
    };
  } else {
    return {
      vulnerabilities: [],
      paginationInfo: null,
    };
  }
}

// One remediation plan per selected vulnerability - each plan only targets the devices that
// actually have that specific package outdated, instead of one combined plan spanning every
// selected row's devices.
export async function handlePatchifiRemediation(params: {
  selectedVulnerabilities: Record<string, Vulnerability>;
  selectedTenantId: string;
  addToast: (message: string, type: string, duration: number, title?: string) => void;
  getSoftwareAssetSummary: (tenantId: string, id: string) => Promise<any>;
  getSoftwareAssetDevices: (tenantId: string, id: string) => Promise<any>;
  createRemediationPlan: (params: any) => Promise<any>;
  onProgress?: (update: RemediationItemProgress) => void;
}) {
  const vulnerabilities = Object.values(params.selectedVulnerabilities);
  const selectedCount = vulnerabilities.length;

  params.addToast(`Processing ${selectedCount} vulnerabilit${selectedCount > 1 ? 'ies' : 'y'} for remediation...`, 'info', 3000);

  const reasonCounts = {
    missingSummary: 0,
    alreadyLatestVersion: 0,
    missingDevices: 0,
    requestFailed: 0,
  };
  let successfullyCreated = 0;

  for (const vulnerability of vulnerabilities) {
    const key = vulnerability.rowKey;
    const title = vulnerability.title || vulnerability.cveId || 'Unknown vulnerability';
    const cveId = vulnerability.cveId;

    try {
      params.onProgress?.({ key, title, cveId, stage: 'processing', message: 'Checking current version…' });

      const summaryResponse = await params.getSoftwareAssetSummary(params.selectedTenantId, vulnerability.softwareAssetInventoryId);

      if (!summaryResponse?.isSuccess || !summaryResponse?.data) {
        reasonCounts.missingSummary += 1;
        params.onProgress?.({ key, title, cveId, stage: 'skipped', message: 'Missing version info' });
        continue;
      }

      const summary = summaryResponse.data;
      const currentVersion = summary.softwareAssetVersion;
      const latestVersion = summary.latestPackageVersion;

      if (currentVersion === latestVersion) {
        reasonCounts.alreadyLatestVersion += 1;
        params.onProgress?.({ key, title, cveId, stage: 'skipped', message: 'Already on latest version' });
        continue;
      }

      params.onProgress?.({ key, title, cveId, stage: 'processing', message: 'Finding target devices…' });

      const devicesResponse = await params.getSoftwareAssetDevices(params.selectedTenantId, vulnerability.softwareAssetInventoryId);
      const deviceIds = new Set<string>();
      if (devicesResponse?.isSuccess && devicesResponse?.data?.assetByAgentDetails) {
        devicesResponse.data.assetByAgentDetails.forEach((device: any) => {
          if (device.tenantAgentID) {
            deviceIds.add(device.tenantAgentID);
          }
        });
      }

      if (deviceIds.size === 0) {
        reasonCounts.missingDevices += 1;
        params.onProgress?.({ key, title, cveId, stage: 'skipped', message: 'No devices found' });
        continue;
      }

      params.onProgress?.({
        key,
        title,
        cveId,
        stage: 'processing',
        message: `Creating plan for ${deviceIds.size} device${deviceIds.size !== 1 ? 's' : ''}…`,
      });

      const planName = `Vulnerability Remediation - ${summary.packageIdentifier || vulnerability.title || vulnerability.cveId} - ${new Date().toLocaleDateString()}`;
      const remediationPlanData = {
        PlanName: planName,
        IsEmergencyRemediationPlan: false,
        SendEmailOnCompletion: false,
        RemediationPlanStartTime: new Date().toISOString(),
        IsActive: true,
      };

      const response = await params.createRemediationPlan({
        remediationPlanPatchList: [{ packageIdentifier: summary.packageIdentifier, targetVersion: latestVersion }],
        targetDeviceIds: Array.from(deviceIds),
        remediationPlanData,
      });

      if (response) {
        successfullyCreated += 1;
        params.onProgress?.({ key, title, cveId, stage: 'success', message: 'Remediation plan created' });
      } else {
        reasonCounts.requestFailed += 1;
        params.onProgress?.({ key, title, cveId, stage: 'failed', message: 'Request failed' });
      }
    } catch (error) {
      console.error(`Error processing vulnerability ${vulnerability.cveId}:`, error);
      reasonCounts.requestFailed += 1;
      params.onProgress?.({ key, title, cveId, stage: 'failed', message: 'Unexpected error' });
    }
  }

  const reasons: string[] = [];
  if (reasonCounts.alreadyLatestVersion > 0) reasons.push(`Already latest version: ${reasonCounts.alreadyLatestVersion}`);
  if (reasonCounts.missingSummary > 0) reasons.push(`Missing version info: ${reasonCounts.missingSummary}`);
  if (reasonCounts.missingDevices > 0) reasons.push(`No devices found: ${reasonCounts.missingDevices}`);
  if (reasonCounts.requestFailed > 0) reasons.push(`Create remediation plan request failed: ${reasonCounts.requestFailed}`);

  const remediationSummary: RemediationSummary = {
    totalRequested: selectedCount,
    successfullyCreated,
    failedOrNotEligible: selectedCount - successfullyCreated,
    reasons,
  };

  const successPercentage = selectedCount > 0 ? Math.round((successfullyCreated / selectedCount) * 100) : 0;
  let toastMessage = '';

  if (successfullyCreated === selectedCount) {
    toastMessage = `✓ All ${selectedCount} remediation plan${selectedCount !== 1 ? 's' : ''} created successfully!`;
  } else if (successfullyCreated > 0) {
    toastMessage = `${successfullyCreated}/${selectedCount} remediation plan${selectedCount !== 1 ? 's' : ''} created (${successPercentage}% success)`;
    if (reasons.length > 0) {
      toastMessage += ` • ${reasons.join(' • ')}`;
    }
  } else {
    toastMessage = `Failed to create remediation plans. ${reasons.length > 0 ? reasons.join(' • ') : 'Please try again.'}`;
  }

  params.addToast(toastMessage, successfullyCreated > 0 ? 'success' : 'warning', 6000, 'Remediation Summary');

  return { remediationSummary, clearSelection: successfullyCreated > 0 };
}
